import { useEffect, useMemo, useState } from "react";
import { examStageForGrade } from "@shared/territories";
import { useTerritory } from "@/contexts/TerritoryContext";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ClipboardList, Clock, Layers, Loader2, PlayCircle, Sparkles, Star, BookOpen, PenLine, BadgeCheck, Hourglass } from "lucide-react";
import { Illustration } from "@/components/Illustration";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { subjectUi, LEVEL_UI, STATUS_LABEL, type TaskSummary, type TaskStats } from "@/lib/performanceTasks";

const SUBJECT_ORDER = ["Mathematics", "Language Arts", "Science", "Social Studies"];

export default function PerformanceTasks() {
  const { user, isAuthenticated } = useAuth();
  const { territory } = useTerritory();
  const [, setLocation] = useLocation();
  const [subject, setSubject] = useState("all");
  // NOT clamped to 4 to 6. That clamp is what served a Grade 11 student four Grade 6 PEP tasks
  // under a heading that said Paper 2, CSEC. Use the grade the student is actually in; if there
  // are no tasks for it yet, the empty state says so honestly.
  const userGrade = Number(user?.grade) || 6;
  const grade = userGrade;
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const { data: tasks = [], isLoading } = useQuery<TaskSummary[]>({ queryKey: ["/api/performance-tasks", grade], queryFn: async () => { const r = await fetch(`/api/performance-tasks?grade=${grade}`, { credentials: "include" }); if (!r.ok) throw new Error("load"); return r.json(); }, enabled: isAuthenticated, refetchInterval: q => (q.state.data?.some(t => t.latest?.status === "submitted") ? 20_000 : false) });
  const { data: stats } = useQuery<TaskStats>({ queryKey: ["/api/performance-tasks/stats", grade], queryFn: async () => { const r = await fetch(`/api/performance-tasks/stats?grade=${grade}`, { credentials: "include" }); if (!r.ok) throw new Error("load"); return r.json(); }, enabled: isAuthenticated });

  // Derived from the tasks themselves. A fixed PEP list meant a CSEC subject such as English A
  // could never show a filter chip even when tasks existed for it.
  const subjects = useMemo(() => {
    const present = Array.from(new Set(tasks.map(t => t.subject)));
    const known = SUBJECT_ORDER.filter(s => present.includes(s));
    const rest = present.filter(s => !SUBJECT_ORDER.includes(s)).sort();
    return [...known, ...rest];
  }, [tasks]);
  const visible = subject === "all" ? tasks : tasks.filter(t => t.subject === subject);
  // What this student's grade actually sits. The page was hard-wired to PEP, so a CSEC student
  // opening Paper 2 was told it was "the real PEP format" and marked "against the PEP rubric".
  const stage = examStageForGrade(user?.grade);
  const isPrimary = stage === "PRIMARY";
  const isLowerSecondary = stage === "LOWER_SECONDARY";
  const paperName = isPrimary ? "Performance Tasks" : isLowerSecondary ? "Term Tests" : "Paper 2";
  // Grades 7 to 9 sit no external exam. Calling their work a PEP or CSEC format would be simply
  // untrue, so the copy names the curriculum they are actually being tested on.
  const examName = isPrimary ? territory.examName
    : isLowerSecondary ? "school term test"
    : stage === "CAPE" ? "CAPE" : "CSEC";

  const resume = tasks.find(t => t.inProgressId);
  const pct = stats && stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-primary text-primary-foreground p-4 md:p-6">
        <div className="container max-w-5xl mx-auto">
          <Button variant="ghost" className="text-white hover:bg-white/20 p-2 mb-2" onClick={() => setLocation("/dashboard")} data-testid="button-back-dashboard"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <Illustration name="owl-cap" size={84} className="hidden sm:block shrink-0" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ClipboardList className="w-6 h-6" /> {paperName}</h1>
                <p className="text-white/90 mt-1 max-w-xl">The real {examName} format: read the sources, work through each part, show your working and write your answers. Owlbert marks it and tells you how to do better.</p>
              </div>
            </div>
            {stats && stats.total > 0 && (
              <div className="bg-white/15 rounded-2xl px-4 py-3 min-w-[230px]">
                <div className="flex justify-between text-sm mb-1"><span>{stats.completed}/{stats.total} done</span><span>{pct}%</span></div>
                <Progress value={pct} className="h-2 bg-white/25" />
                <p className="text-xs text-white/80 mt-1">{stats.xpEarned} XP earned{stats.awaiting ? ` · ${stats.awaiting} being marked` : ""}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* No grade chips here any more. There is one grade switcher, in the top bar, and it is
            admin-only. Having a second one on this page meant two controls owned the same idea and
            could disagree: the bar could say Grade 11 while the page listed Grade 6 papers, which
            is exactly how PEP content kept surfacing under a CSEC heading. Subject filters stay,
            because those are a genuine within-page filter rather than a change of identity. */}
        <div className="flex flex-wrap items-center gap-2">
          {subjects.length > 1 && ["all", ...subjects].map(s => (
            <button key={s} type="button" onClick={() => setSubject(s)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold border-2 transition-colors ${subject === s ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:border-primary/50"}`} data-testid={`filter-subject-${s}`}>
              {s === "all" ? "All subjects" : s}
            </button>
          ))}
        </div>

        {resume && (
          <Card className="rounded-2xl border-primary/30">
            <CardContent className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <Illustration name="owl-book" size={72} className="shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">You have a task in progress</p>
                <p className="text-sm text-muted-foreground">{resume.title}. Your answers are saved, pick up where you left off.</p>
              </div>
              <Button className="rounded-xl font-bold" onClick={() => setLocation(`/performance-tasks/${resume.slug}?attempt=${resume.inProgressId}`)} data-testid="button-resume-task"><PlayCircle className="w-4 h-4 mr-2" /> Continue</Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && tasks.length > 0 && tasks.every(t => t.attempts === 0) && (
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { icon: BookOpen, title: "1. Read the sources", text: "Articles, letters, tables and graphs. Jot your important points as you go." },
              { icon: PenLine, title: "2. Work through the parts", text: "Choose, match, calculate and write. Show your working, it earns marks." },
              { icon: BadgeCheck, title: "3. Get marked", text: `Owlbert marks your work against the ${examName} rubric and shows you what to fix.` },
            ].map(s => (
              <div key={s.title} className="rounded-xl border border-border bg-card p-4 flex gap-3">
                <span className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0"><s.icon className="w-5 h-5" /></span>
                <div><p className="font-semibold text-sm">{s.title}</p><p className="text-xs text-muted-foreground mt-0.5">{s.text}</p></div>
              </div>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : visible.length === 0 ? (
          <Card className="rounded-xl"><CardContent className="p-4 md:p-5">
            <StudentEmptyState illustration="owl-search" title={`No Grade ${grade} tasks here yet`} hint="Owlbert is still writing them. Practise a quiz for now." action={<Button className="rounded-lg" onClick={() => setLocation("/learning")}>Go to Subjects</Button>} />
          </CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {visible.map(t => <TaskCard key={t.id} task={t} onOpen={(q) => setLocation(`/performance-tasks/${t.slug}${q || ""}`)} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, onOpen }: { task: TaskSummary; onOpen: (query?: string) => void }) {
  const ui = subjectUi(task.subject);
  const latest = task.latest;
  const level = task.best?.level;
  let cta = "Start task", query = "", icon = <PlayCircle className="w-4 h-4 mr-2" />;
  if (task.inProgressId) { cta = "Continue"; query = `?attempt=${task.inProgressId}`; }
  else if (latest && latest.status !== "in_progress") { cta = latest.status === "graded" ? "See results" : "See progress"; query = `?attempt=${latest.id}`; icon = <Star className="w-4 h-4 mr-2" />; }
  return (
    <Card className={`rounded-xl border ${ui.card} flex flex-col`} data-testid={`card-task-${task.slug}`}>
      <CardContent className="p-4 md:p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start gap-3">
          <span className={`w-11 h-11 rounded-xl ${ui.sticker} flex items-center justify-center shrink-0 -rotate-2`}><ClipboardList className="w-6 h-6" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap gap-1.5 mb-1">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ui.chip}`}>{task.subject}</span>
              <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-muted">Grade {task.grade}</span>
              {latest && latest.status !== "in_progress" && latest.status !== "graded" && <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-500/15 text-amber-800 dark:text-amber-200 inline-flex items-center gap-1"><Hourglass className="w-3 h-3" /> {STATUS_LABEL[latest.status]}</span>}
              {level && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${LEVEL_UI[level].chip}`}>{level}</span>}
            </div>
            <h3 className="font-bold leading-snug">{task.title}</h3>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{task.description}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-auto">
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {task.timeMinutes} min</span>
          <span className="inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {task.partCount} parts, {task.itemCount} questions</span>
          <span className="inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> up to {task.xpReward} XP</span>
        </div>
        {task.best && task.best.percent !== null && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs font-medium"><span>Best score</span><span className="tabular-nums">{task.best.score} / {task.totalPoints} ({Math.round(task.best.percent)}%)</span></div>
            <div className={`h-2 rounded-full ${ui.card}`}><div className={`h-2 rounded-full ${LEVEL_UI[task.best.level || "Beginning"].bar}`} style={{ width: `${task.best.percent}%` }} /></div>
          </div>
        )}
        <div className="flex gap-2">
          <Button className={`rounded-lg font-semibold flex-1 ${ui.button}`} onClick={() => onOpen(query)} data-testid={`button-task-${task.slug}`}>{icon}{cta}</Button>
          {latest && latest.status !== "in_progress" && <Button variant="outline" className="rounded-lg" onClick={() => onOpen("")} data-testid={`button-task-again-${task.slug}`}>Try again</Button>}
        </div>
      </CardContent>
    </Card>
  );
}
